import React, { useState, useEffect, useCallback } from "react";
import { sortBy } from "lodash";
import {
  Button,
  Checkbox,
  DatePicker,
  DatePickerInput,
  ContentSwitcher,
  Switch,
  Modal,
  TextInput,
  Select,
  SelectItem,
} from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import moment from "moment";

import {
  DEFAULT_SETTLEMENT_AGENT,
  DisclosureFee,
  DisclosureType,
  DISCLOSURE_TYPE_SWITCH_OPTIONS,
  AppShape,
  operatorHumanizeLoanProgramName,
  ClosingSummaryUpdateRequest,
} from "@eave/shared/types/api";
import { eaveLegalName, SFAddress } from "@eave/shared/config/contacts";
import { addressToStr } from "@eave/shared/utils/addresses";

import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import {
  appToBorrower1Name,
  appToPropertyAddress,
  selectableCOECategories,
} from "underwriter_dashboard/shared";
import { fetchWorksheetPrepaids } from "underwriter_dashboard/hurdle_editors/Prepaids/api";
import { fetchWorksheetEscrows } from "underwriter_dashboard/hurdle_editors/Escrows/api";

import APIClient from "web_modules/APIClient";
import ClickTo from "web_modules/shared/CollapseExpandToggle";
import Money from "web_modules/Money";
import LabeledObject from "web_modules/shared/LabeledObject";

import { titleCaseNoSpaces } from "../../AppEditor";
import { isSettlementCoAffiliate } from "./helpers";
import FeeTable from "./FeeTable";
import Prorations from "./Prorations";
import SellerCredits from "./SellerCredits";
import TRIDForm from "./TRIDForm";
import LenderCredits from "./LenderCredits";
import Adjustments from "./Adjustments";

const hurdleEditorCommonStyles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");
const styles = require("./styles.module.scss");

interface HurdleEditorProps extends HurdleProps {
  dispatch?: Function;
}

function prepaidItemToDisclosureFee(item) {
  return {
    actual_amount: item.amount,
    cd_section: "F",
    contact_id: null,
    created_at: "",
    deleted_at: null,
    description: item.recipient,
    estimated_amount: item.amount,
    final_le_amount: Number(item.amount),
    id: item.id,
    included_in_apr: false,
    le_section: "F",
    loan_application_id: item.loan_application_id,
    mismo_type: item.item_type,
    paid_before_closing: false,
    paid_by_type: item.paid_by,
    paid_to_type: item.paid_to,
    recipient: item.recipient,
    root_id: item.id,
    source: "",
    tolerance: "",
    unsaved: false,
    valid: false,
  };
}

function escrowItemToDisclosureFee(item) {
  return {
    actual_amount: Number(item.monthly_amount) * item.months_collected,
    cd_section: "G",
    description: item.recipient,
    estimated_amount: Number(item.monthly_amount) * item.months_collected,
    final_le_amount: Number(item.monthly_amount) * item.months_collected,
    id: item.id,
    included_in_apr: false,
    le_section: "G",
    mismo_type: item.type,
    paid_before_closing: false,
    paid_by_type: "Buyer",
    paid_to_type: "ThirdPartyProvider",
    recipient: item.recipient,
    root_id: item.id,
    unsaved: false,
    valid: false,
  };
}

const Fees: React.FC<any> = ({ app, hurdle, onHide }: HurdleEditorProps) => {
  // Fetch preference, set to LoanEstimate if none is set
  const disclosureTypeKey = "disclosureType";
  const disclosureType =
    (localStorage.getItem(disclosureTypeKey) as DisclosureType) || DisclosureType.LoanEstimate;
  const [selectedDisclosure, updateSelectedDisclosure] = useState<DisclosureType>(disclosureType);

  const [fees, updateFees] = useState([]);
  const [prepaids, setPrepaids] = useState<DisclosureFee[]>();
  const [escrows, setEscrows] = useState<DisclosureFee[]>();

  const [tridCure, updateTRIDCure] = useState(null);

  const [isSettlementAffiliate, setIsSettlementAffiliate] = useState(
    isSettlementCoAffiliate(app.contacts.settlement_agent_company),
  );
  const [wasSettlementAffiliateSet, setWasSettlementAffiliateSet] = useState(false);

  const [signingDate, setSigningDate] = useState(app.computed_properties.signing_date);
  const [closingDate, setClosingDate] = useState(app.computed_properties.closing_date);
  const [closingDateChangeReason, setClosingDateChangeReason] = useState("");
  const [closingDateChangeCategory, setClosingDateChangeCategory] = useState("");
  const [disbursementDate, setDisbursementDate] = useState(
    app.computed_properties.disbursement_date,
  );
  const [showCOEReasonModal, setShowCOEReasonModal] = useState(false);

  const defaultFormState = {
    paid_before_closing: false,
  };

  const getContactName = contact => {
    // settlement agent company & title agent company with this name
    // are persisted, if the borrower doesn't have a settlement agent
    // in borrower flow at /settlement_agent.

    if (!contact || contact.name === DEFAULT_SETTLEMENT_AGENT) {
      return "";
    }

    return contact.name;
  };

  const getRecipientOptions = (application: AppShape) => {
    const currentRecipients = fees.map(fee => fee.recipient);
    return [
      ...currentRecipients,
      getContactName(application.contacts.settlement_agent_company),
      getContactName(application.contacts.title_company),
      getContactName(application.contacts.seller_real_estate_agent_company),
      getContactName(application.contacts.buyer_real_estate_agent_company),
      application.computed_properties.purchased_property_county,
      "Got Appraisals",
      "Accurate Group, LLC",
      "Triserv Appraisal Management Solutions",
    ].filter(v => v);
  };

  const [formState, updateFormState] = useState(defaultFormState as DisclosureFee);

  const [isCollapsed, updateIsCollapsed] = useState(false);

  // Restores the form state back to default initial state
  const resetForm = () => {
    updateFormState(defaultFormState as DisclosureFee);
  };
  const { contacts, disclosures } = app as AppShape;

  const calculateIssuedDate = () => {
    return (
      disclosureType === DisclosureType.ClosingDisclosure &&
      app.computed_properties &&
      app.computed_properties.closing_date
    );
  };

  const loadTRIDCure = useCallback(() => {
    APIClient.get(`/uw/loan_applications/${app.id}/trid_cure`).then((response: any) => {
      const { trid_cure } = response;
      updateTRIDCure(trid_cure);
    });
  }, [app.id]);

  const loadPrepaids = useCallback(() => {
    fetchWorksheetPrepaids(app).then(p => {
      const items = p.items.map(item => prepaidItemToDisclosureFee(item));
      setPrepaids(items);
    });
  }, [app.id]);

  const loadEscrows = useCallback(() => {
    fetchWorksheetEscrows(app).then(escrows => {
      const items = escrows.items.map(item => escrowItemToDisclosureFee(item));
      setEscrows(items);
    });
  }, [app.id]);

  const loadFees = useCallback(() => {
    APIClient.get(`/uw/${app.id}/fees`).then((response: any) => {
      const { fees } = response;

      if (fees !== null) {
        updateFees(
          sortBy(
            fees.map((fee: DisclosureFee) => ({ ...fee, unsaved: false })),
            (fee: any) => fee.description + fee.mismo_type,
          ),
        );
      }
    });
  }, [app.id]);

  // Initial fetching
  useEffect(() => {
    loadPrepaids();
    loadEscrows();
    loadFees();
    loadTRIDCure();
  }, [loadFees, loadTRIDCure, loadPrepaids, loadEscrows]);

  const saveIsSettlementAffiliateIfNeeded = () => {
    if (!wasSettlementAffiliateSet) {
      return [];
    }

    return [
      {
        role: "SettlementAgentCompany",
        contact: app.contacts.settlement_agent_company,
      },
      {
        role: "SettlementAgent",
        contact: app.contacts.settlement_agent,
      },
    ].map(v => {
      const updated = {
        ...v.contact,
        source: "ContactInfoHurdle", // :( consistent across upserts
        loan_application_id: app.id,
        role: v.role,
        is_affiliate: isSettlementAffiliate,
      };
      return APIClient.post(`/contacts`, updated);
    });
  };

  const saveClosingDates = () => {
    const { signing_date, closing_date, disbursement_date } = app.computed_properties;
    const request: ClosingSummaryUpdateRequest = {};
    if (closingDate !== closing_date) {
      request.closing_date = closingDate;
    }
    if (closingDateChangeReason !== "") {
      request.closing_date_change_reason = closingDateChangeReason;
    }
    if (closingDateChangeCategory !== "") {
      request.closing_date_change_category = closingDateChangeCategory;
    }
    if (signingDate !== signing_date) {
      request.operator_signing_date = signingDate;
    }

    if (disbursementDate !== disbursement_date) {
      request.operator_disbursement_date = disbursementDate;
    }

    return APIClient.put(`/loan_applications/${app.id}`, request);
  };

  const closingDatesChanged = () => {
    const { signing_date, closing_date, disbursement_date } = app.computed_properties;
    if (signing_date && signing_date !== signingDate) {
      return true;
    }
    if (closing_date && closing_date !== closingDate) {
      return true;
    }
    if (disbursement_date && disbursement_date !== disbursementDate) {
      return true;
    }
    return false;
  };

  const cancelDateChangeReasonModal = () => {
    setShowCOEReasonModal(false);
    setClosingDateChangeReason("");
    setClosingDateChangeCategory("");
  };

  const onSave = () => {
    let saveClosingDatesPromise = Promise.resolve(null);
    if (closingDatesChanged()) {
      if (
        closingDateChangeReason === "" &&
        closingDateChangeCategory === "" &&
        closingDate !== app.computed_properties.closing_date &&
        app.target_closing_date
      ) {
        // COE Date has changed from the Target Date
        setShowCOEReasonModal(true);
        return;
      }
      saveClosingDatesPromise = saveClosingDates();
    }

    Promise.all([saveClosingDatesPromise, ...saveIsSettlementAffiliateIfNeeded()])
      .then(() => {
        // always reload, even if don't need to persist,
        // since reloading refreshes state -- which is needed
        // to show computed field changes, for example,
        // in TRID cure.
        window.location.reload();
      })
      .catch(() => {
        alert(
          `there was an error saving the changes, please contact engineering or reload the page and try again`,
        );
        throw new Error(
          `there was an error saving the fees hurdle changes to loan application Id: ${app.id}.`,
        );
      });
  };

  const lenderCreditAmount = app.lender_credits.reduce(
    (acc, credit) => acc + Number(credit.amount),
    0,
  );

  return (
    <HurdleView
      className={styles.disclosuresHurdle}
      app={app}
      hurdle={hurdle}
      onHide={onHide}
      hideTitle
    >
      <h1 className={styles.title}>Fees</h1>
      <ContentSwitcher
        className={styles.switcher}
        size="sm"
        onChange={action => {
          updateSelectedDisclosure(action.name);
          localStorage.setItem(disclosureTypeKey, action.name);
        }}
        selectedIndex={DISCLOSURE_TYPE_SWITCH_OPTIONS.map(item => item.name).indexOf(
          selectedDisclosure as DisclosureType,
        )}
      >
        {DISCLOSURE_TYPE_SWITCH_OPTIONS.map(item => (
          <Switch key={item.name} name={item.name} text={item.text} />
        ))}
      </ContentSwitcher>

      <div className={styles.loanInfoParent}>
        <div
          style={{ position: isCollapsed ? "absolute" : "relative", display: "flex" }}
          className={styles.loanInfoTitle}
        >
          <h2 className={styles.loanInfoHeading}>Loan Information</h2>
          {isCollapsed && (
            <div className={styles.loanInfoHeadingIcon}>
              <ClickTo.Collapse
                isCollapsed={isCollapsed}
                collapseToggleFunc={() => updateIsCollapsed(false)}
              />
            </div>
          )}
          {!isCollapsed && (
            <div className={styles.loanInfoHeadingIcon}>
              <ClickTo.Collapse
                collapseToggleFunc={() => updateIsCollapsed(true)}
                isCollapsed={isCollapsed}
              />
            </div>
          )}
        </div>
        {isCollapsed && <ClickTo.Expand collapseToggleFunc={() => updateIsCollapsed(false)} />}
        {!isCollapsed && (
          <div className={styles.loanInfoContainer}>
            {selectedDisclosure === DisclosureType.ClosingDisclosure && (
              <>
                <div className={styles.loanInfoQuadrant}>
                  <LabeledObject
                    size="compact"
                    title="Settlement Info"
                    listData={[
                      {
                        label: "Settlement Company",
                        value:
                          contacts && contacts.settlement_agent_company ? (
                            contacts.settlement_agent_company.name
                          ) : (
                            <span>&mdash;</span>
                          ),
                      },
                      {
                        label: "Company Phone",
                        value:
                          contacts && contacts.settlement_agent_company ? (
                            contacts.settlement_agent_company.phone
                          ) : (
                            <span>&mdash;</span>
                          ),
                      },
                      {
                        label: "Contact Name",
                        value:
                          contacts && contacts.settlement_agent ? (
                            contacts.settlement_agent.name
                          ) : (
                            <span>&mdash;</span>
                          ),
                      },
                      {
                        label: "Contact Phone",
                        value:
                          contacts &&
                          contacts.settlement_agent &&
                          contacts.settlement_agent.phone ? (
                            contacts.settlement_agent.phone
                          ) : (
                            <span>&mdash;</span>
                          ),
                      },
                      {
                        label: "Source",
                        value:
                          contacts && contacts.settlement_agent_company ? (
                            contacts.settlement_agent_company.source
                          ) : (
                            <span>&mdash;</span>
                          ),
                      },
                      {
                        label: "Is affiliate provider?",
                        value: (
                          <Checkbox
                            id="is-affiliate-provider"
                            labelText=""
                            checked={isSettlementAffiliate}
                            onChange={() => {
                              setIsSettlementAffiliate(!isSettlementAffiliate);
                              setWasSettlementAffiliateSet(true);
                            }}
                          />
                        ),
                      },
                    ]}
                  />
                </div>
                <div className={styles.loanInfoQuadrant}>
                  <LabeledObject
                    size="compact"
                    title="Transaction Info"
                    listData={[
                      {
                        label: "Buyer",
                        value:
                          app && app.borrower1_first_name && app.borrower1_last_name ? (
                            <>
                              {appToBorrower1Name(app)}
                              <br />
                              {addressToStr(
                                app.borrower1_current_address_line1,
                                app.borrower1_current_address_line2,
                                app.borrower1_current_city,
                                app.borrower1_current_state,
                                app.borrower1_current_postal_code,
                              )}
                            </>
                          ) : (
                            <span>&mdash;</span>
                          ),
                      },
                      {
                        label: "Seller(s) Information",
                        value:
                          // eslint-disable-next-line no-nested-ternary
                          app && contacts && contacts.seller1 && contacts.seller2 ? (
                            <>
                              {contacts.seller1.name}
                              <br />
                              {addressToStr(
                                contacts.seller1.address_line1,
                                contacts.seller1.address_line2,
                                contacts.seller1.city,
                                contacts.seller1.state,
                                contacts.seller1.postal_code,
                              )}
                              <br />
                              {contacts.seller2.name}
                              <br />
                              {addressToStr(
                                contacts.seller2.address_line1,
                                contacts.seller2.address_line2,
                                contacts.seller2.city,
                                contacts.seller2.state,
                                contacts.seller2.postal_code,
                              )}
                            </>
                          ) : app && contacts && contacts.seller1 ? (
                            <>
                              {contacts.seller1.name}
                              <br />
                              {addressToStr(
                                contacts.seller1.address_line1,
                                contacts.seller1.address_line2,
                                contacts.seller1.city,
                                contacts.seller1.state,
                                contacts.seller1.postal_code,
                              )}
                            </>
                          ) : (
                            <span>&mdash;</span>
                          ),
                      },
                      {
                        label: "Lender",
                        value:
                          app && contacts && contacts.loan_officer ? (
                            <>
                              {eaveLegalName.formal}
                              <br />
                              {addressToStr(
                                SFAddress.line1,
                                SFAddress.line2,
                                SFAddress.city,
                                SFAddress.state,
                                SFAddress.zip,
                              )}
                            </>
                          ) : (
                            <span>&mdash;</span>
                          ),
                      },
                    ]}
                  />
                </div>
              </>
            )}
            <div className={styles.loanInfoQuadrant}>
              <LabeledObject
                size="compact"
                title="Loan Information"
                listData={[
                  {
                    label: "Loan Term",
                    value: "30 years",
                  },
                  {
                    label: "Purpose",
                    value: app && app.loan_purpose && titleCaseNoSpaces(app.loan_purpose),
                  },
                  {
                    label: "Product",
                    value:
                      app &&
                      app.selected_loan_program &&
                      operatorHumanizeLoanProgramName[app.selected_loan_program],
                  },
                  {
                    label: "Loan Type",
                    value: "Conventional",
                  },
                  {
                    label: "Loan ID #",
                    value: app && app.external_id && String(app.external_id),
                  },
                  {
                    label: "Rate Lock",
                    value:
                      app && app.active_rate && app.active_rate.locked ? (
                        <span>Yes</span>
                      ) : (
                        <span>No</span>
                      ),
                  },
                ]}
              />
            </div>
            {selectedDisclosure === DisclosureType.ClosingDisclosure && (
              <div className={styles.loanInfoQuadrant}>
                <LabeledObject
                  size="compact"
                  title="Closing Info"
                  listData={[
                    {
                      label: "Date Issued",
                      value:
                        app && selectedDisclosure ? (
                          moment(calculateIssuedDate()).format("MM/DD/YYYY")
                        ) : (
                          <span>&mdash;</span>
                        ),
                    },
                    {
                      label: "Closing / Signing Date",
                      value: signingDate ? (
                        <DatePicker
                          id="date-picker-signing-date"
                          datePickerType="single"
                          dateFormat="m/d/Y"
                          style={{ marginBottom: "10px" }}
                          value={moment(signingDate).format("MM/DD/YYYY")}
                          onChange={event => {
                            const newDate =
                              event.length > 0 ? moment(event[0]).format("YYYY-MM-DD") : null;
                            setSigningDate(newDate);
                          }}
                        >
                          <DatePickerInput
                            id="date-picker-signing-date-input-id"
                            name="signing_date"
                            iconDescription="Select a signing date"
                            placeholder="mm-dd-yyyy"
                            type="text"
                            size="sm"
                          />
                        </DatePicker>
                      ) : (
                        <span>&mdash;</span>
                      ),
                    },
                    {
                      label: "Close of Escrow / Recording Date",
                      value: closingDate ? (
                        <DatePicker
                          id="date-picker-closing-date"
                          datePickerType="single"
                          dateFormat="m/d/Y"
                          style={{ marginBottom: "10px" }}
                          value={moment(closingDate).format("MM/DD/YYYY")}
                          onChange={event => {
                            const newDate =
                              event.length > 0 ? moment(event[0]).format("YYYY-MM-DD") : null;
                            setClosingDate(newDate);
                          }}
                        >
                          <DatePickerInput
                            id="date-picker-closing-date-input-id"
                            name="closing_date"
                            iconDescription="Select a COE Date"
                            placeholder="mm-dd-yyyy"
                            type="text"
                            size="sm"
                          />
                        </DatePicker>
                      ) : (
                        <span>&mdash;</span>
                      ),
                    },
                    {
                      label: "Disbursement Date",
                      value: disbursementDate ? (
                        <DatePicker
                          id="date-picker-disbursement-date"
                          datePickerType="single"
                          dateFormat="m/d/Y"
                          style={{ marginBottom: "10px" }}
                          value={moment(disbursementDate).format("MM/DD/YYYY")}
                          onChange={event => {
                            const newDate =
                              event.length > 0 ? moment(event[0]).format("YYYY-MM-DD") : null;
                            setDisbursementDate(newDate);
                          }}
                        >
                          <DatePickerInput
                            id="date-picker-disbursement-date-input-id"
                            name="disbursement_date"
                            iconDescription="Select a disbursement date"
                            placeholder="mm-dd-yyyy"
                            type="text"
                            size="sm"
                          />
                        </DatePicker>
                      ) : (
                        <span>&mdash;</span>
                      ),
                    },
                    {
                      label: "Settlement Agent",
                      value:
                        contacts && contacts.settlement_agent ? (
                          contacts.settlement_agent.name
                        ) : (
                          <span>&mdash;</span>
                        ),
                    },
                    {
                      label: "File #",
                      value:
                        app && app.external_id ? String(app.external_id) : <span>&mdash;</span>,
                    },
                    {
                      label: "Property",
                      value:
                        app && appToPropertyAddress(app) ? (
                          appToPropertyAddress(app)
                        ) : (
                          <span>&mdash;</span>
                        ),
                    },
                    {
                      label: "Sale Price",
                      value:
                        app && app.computed_properties && app.computed_properties.purchase_price ? (
                          <Money value={app.computed_properties.purchase_price} />
                        ) : (
                          <span>&mdash;</span>
                        ),
                    },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <FeeTable
        app={app}
        tridCure={tridCure}
        formState={formState}
        updateFormState={updateFormState}
        resetForm={resetForm}
        fees={fees}
        loadFees={loadFees}
        selectedDisclosure={selectedDisclosure}
        disclosures={disclosures}
        updateSelectedDisclosure={updateSelectedDisclosure}
        lenderCreditAmount={lenderCreditAmount}
        recipientOptions={getRecipientOptions(app)}
        prepaids={prepaids}
        escrows={escrows}
      />
      <div className={styles.editorsContainer} style={{ marginBottom: "50px" }}>
        <div>
          <div style={{ marginBottom: "10px" }} />
          <SellerCredits app={app} />
          <TRIDForm appId={app.id} />
          <LenderCredits appId={app.id} lenderCredits={app.lender_credits} />
        </div>
        {selectedDisclosure === DisclosureType.ClosingDisclosure && (
          <>
            <Prorations app={app} />
            <div style={{ marginBottom: "10px" }} />
            <Adjustments app={app} />
          </>
        )}
      </div>
      <Modal
        open={showCOEReasonModal}
        modalHeading="Close of Escrow Date Change Reason"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        primaryButtonDisabled={closingDateChangeReason === "" || closingDateChangeCategory === ""}
        onBlur={() => cancelDateChangeReasonModal()}
        onRequestClose={() => cancelDateChangeReasonModal()}
        onRequestSubmit={() => onSave()}
      >
        <Select
          light
          id="coe-date-change-category-select"
          name="coe-date-change-category-select"
          labelText="Category"
          placeholder="Select Category"
          value={closingDateChangeCategory}
          onChange={e => setClosingDateChangeCategory(e.target.value)}
        >
          <SelectItem key="no-category" text="Select a category" value="" />
          {Object.values(selectableCOECategories).map(c => (
            <SelectItem key={c.value} text={c.label} value={c.value} />
          ))}
        </Select>
        <TextInput
          id="coe-date-change-reason-text-input"
          value={closingDateChangeReason}
          labelText="Please enter the reason for changing the Close of Escrow Date"
          onChange={e => setClosingDateChangeReason(e.target.value)}
        />
      </Modal>
      <footer className={hurdleEditorCommonStyles.footer}>
        <Button kind="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button onClick={onSave} renderIcon={Save16}>
          Save changes
        </Button>
      </footer>
    </HurdleView>
  );
};

export default Fees;
